'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  Stack,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, GetApp, IosShare, MoreVert } from '@mui/icons-material';
import { themeColors } from '@/theme/theme';
import { useTranslation } from '@/kit/useTranslation';

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  useEffect(() => {
    // Check if already installed
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');

    setIsStandalone(isInStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user previously dismissed
    const wasDismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    }

    // Don't show if already installed
    if (isInStandaloneMode) return;

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 3 seconds on page
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show after 3 seconds if not installed
    if (iOS && !isInStandaloneMode) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);

    if (outcome === 'accepted') {
      localStorage.setItem('pwa-installed', 'true');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't show if:
  // - Already installed
  // - User dismissed recently
  // - Not mobile (optional - you can remove this to show on desktop too)
  if (isStandalone || dismissed || !showPrompt) {
    return null;
  }

  // iOS Instructions
  if (isIOS) {
    return (
      <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            maxWidth: 420,
            mx: 'auto',
            borderRadius: 3,
            p: 2.5,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '2px solid',
            borderColor: themeColors.primaryColor,
            zIndex: 1300,
            boxShadow: '0 12px 48px rgba(1, 97, 31, 0.2)',
          }}
        >
          <Stack spacing={2}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: themeColors.primaryColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <GetApp sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700} color="text.primary">
                    Install OFC App
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('pwa.homeScreenAccess')}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" onClick={handleDismiss} aria-label="Dismiss install prompt">
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ bgcolor: '#f1f5f9', borderRadius: 2, p: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('pwa.toInstall')}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    1.
                  </Typography>
                  <Typography variant="body2">
                    {t('pwa.iosStep1')}{' '}
                    <IosShare sx={{ fontSize: 16, verticalAlign: 'middle', mx: 0.5 }} />
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    2.
                  </Typography>
                  <Typography variant="body2">{t('pwa.iosStep2')}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Slide>
    );
  }

  // Android/Chrome Install Prompt
  return (
    <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          maxWidth: 420,
          mx: 'auto',
          borderRadius: 3,
          p: 2.5,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '2px solid',
          borderColor: themeColors.primaryColor,
          zIndex: 1300,
          boxShadow: '0 12px 48px rgba(1, 97, 31, 0.2)',
        }}
      >
        <Stack spacing={2}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: themeColors.primaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <GetApp sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                  Install OFC App
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('pwa.fullAppExperience')}
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={handleDismiss} aria-label="Dismiss install prompt">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('pwa.offlineFasterPush')}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleDismiss}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                borderColor: '#e5e7eb',
                color: 'text.secondary',
              }}
            >
              {t('pwa.notNow')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleInstallClick}
              disabled={!deferredPrompt}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: themeColors.primaryColor,
                '&:hover': {
                  bgcolor: themeColors.primaryColor,
                  opacity: 0.9,
                },
              }}
            >
              {t('pwa.installNow')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Slide>
  );
}
